'use client';

/**
 * L'onde de la dictée, comme celle de ChatGPT — reprise de Drakkar (07/10). Elle
 * se pose là où l'œil est déjà : sur le champ que la dictée va remplir, ou sous le
 * micro quand il n'y a pas de champ. Rien n'est rendu hors enregistrement.
 *
 * `level` est l'amplitude lissée du micro (`meterStream`, `dicter`), réécrite à
 * chaque image : on n'en garde que le pic de chaque pas, la toile est repeinte au
 * pas seulement. La couleur est celle du texte (`currentColor`), à fixer par
 * `className` comme la taille.
 */
import { useEffect, useRef } from 'react';
import { barresOnde, ONDE_LARGE, ONDE_PAS_MS } from '@/lib/dictee';

export function OndeDictee({ actif, level, className = '' }: { actif: boolean; level: number; className?: string }) {
  const toile = useRef<HTMLCanvasElement>(null);
  const pic = useRef(0);
  // Même redressement que le bouton micro : une voix normale vit entre 0,05 et
  // 0,3, sans lui les barres resteraient au ras du sol.
  const v = Math.pow(Math.min(1, Math.max(0, level)), 0.55);
  useEffect(() => { if (v > pic.current) pic.current = v; }, [v]);

  useEffect(() => {
    if (!actif) return;
    const pics: number[] = [];
    pic.current = 0;
    const minuterie = setInterval(() => {
      const c = toile.current;
      if (!c) return;
      pics.push(pic.current);
      pic.current = 0;
      const r = devicePixelRatio || 1, w = c.clientWidth, h = c.clientHeight;
      if (c.width !== Math.round(w * r) || c.height !== Math.round(h * r)) {
        c.width = Math.round(w * r); c.height = Math.round(h * r);
      }
      const g = c.getContext('2d');
      if (!g) return;
      g.setTransform(r, 0, 0, r, 0, 0);
      g.clearRect(0, 0, w, h);
      g.fillStyle = getComputedStyle(c).color;
      const barres = barresOnde(pics, w, h);
      pics.splice(0, pics.length - barres.length);
      for (const b of barres) {
        g.beginPath();
        g.roundRect(b.x, b.y, ONDE_LARGE, b.h, ONDE_LARGE / 2);
        g.fill();
      }
    }, ONDE_PAS_MS);
    return () => clearInterval(minuterie);
  }, [actif]);

  if (!actif) return null;
  // L'enveloppe porte le placement : une toile placée en absolu sans largeur
  // explicite prend celle de son bitmap, qu'on agrandit à chaque pas — elle
  // grandissait sans fin et les barres sortaient du cadre (07/10).
  return (
    <div aria-hidden className={`pointer-events-none ${className}`}>
      <canvas ref={toile} className="block w-full h-full" />
    </div>
  );
}
